import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { Flex } from "@domestika-dev/prudencio/dist/atoms/Layout";
import { useLoadAssets } from "@web/react-hooks";
import { LiveSessionContext } from "../../../context/LiveSessionContext";
import { LiveSessionAlert } from "../LiveSessionAlert";
import {
  insertScriptAsync,
  removeNode,
  removeHeadNode,
} from "../../../../../../js/utils/functions";
import * as LiveSessionVideoAlertTypes from "../../../constants/liveSessionVideoAlertTypes";
import { LiveSessionVideoAlerts } from "../../../constants/liveSessionVideoAlerts";
import * as UserRoles from "../../../constants/roles";
import { config } from "../../../config";
import { isLowQualityBitrate, isStreamlabsError } from "../../../utils/liveSessionPlayer";
import { canPlayLiveRecording } from "../../../utils/liveSessionStates";

const alertInitialState = {
  show: false,
  key: null,
  message: null,
  type: null,
};

export const VideoJSVideoPlayer = ({ width, height, onReady, onEnded, onStreamError }) => {
  const { liveSession, recording, isTeacher, streaming } = useContext(LiveSessionContext);
  useLoadAssets([config.VIDEOJS_STYLE_URL].filter(Boolean));

  const [player, setPlayer] = useState();
  const [alert, _setAlert] = useState();

  const videoPlayerRef = useRef(null);
  const alertRef = useRef(alert);
  const setAlert = (data) => {
    alertRef.current = data;
    _setAlert(data);
  };

  const isRecordedLive = useCallback(
    () =>
      canPlayLiveRecording(liveSession?.aasmState) && liveSession?.hasRecording && recording?.url,
    [liveSession, recording]
  );

  const getVideoUrl = useCallback(
    () =>
      isRecordedLive()
        ? recording.url
        : `${streaming?.playbackUrl}?token=${streaming?.playerToken}`,
    [liveSession, recording, streaming]
  );

  const setupPlayer = () => {
    if (window.registerIVSTech && window.videojs) {
      window.registerIVSTech(window.videojs);

      let qualityPluginRegistered = false;
      if (isRecordedLive() && window.registerIVSQualityPlugin) {
        window.registerIVSQualityPlugin(window.videojs);
        qualityPluginRegistered = true;
      }

      const videoJsPlayer = window.videojs(
        videoPlayerRef.current,
        {
          controls: true,
          controlBar: {
            pictureInPictureToggle: false,
          },
          muted: true,
          autoplay: true,
          volume: 0.5,
          bigPlayButton: true,
          loadingSpinner: true,
          responsive: true,
          fill: true,
          techOrder: ["AmazonIVS"],
        },
        function ready() {
          /* Disable Pause/Play click on <video /> */
          /* eslint-disable-next-line no-underscore-dangle */
          videoJsPlayer.player_.handleTechClick_ = () => {};

          if (qualityPluginRegistered) {
            videoJsPlayer.enableIVSQualityPlugin();
          }

          videoJsPlayer.src(getVideoUrl());
          videoJsPlayer.play();
          setPlayer(videoJsPlayer);
        }
      );
    }

    return null;
  };

  const setupAlert = ({ key, show }) => {
    if (show) {
      const { message, type } = LiveSessionVideoAlerts[key];
      setAlert({
        show: true,
        key,
        message,
        type,
      });
    } else if (key === alertRef.current?.key) {
      setAlert(alertInitialState);
    }
  };

  const getIVSPlayerEvents = useCallback(() =>
    [
      {
        userRoles: [UserRoles.TEACHER],
        eventName: player?.getIVSEvents()?.PlayerEventType?.QUALITY_CHANGED,
        callback: (quality) => {
          setupAlert({
            key: LiveSessionVideoAlertTypes.TEACHER_LOW_BITRATE_QUALITY,
            show: isLowQualityBitrate(quality?.bitrate || 0),
          });
        },
      },
      {
        userRoles: [UserRoles.TEACHER],
        eventName: player?.getIVSEvents()?.PlayerState?.READY,
        callback: onReady,
      },
      {
        userRoles: [UserRoles.TEACHER],
        eventName: player?.getIVSEvents()?.PlayerState?.ENDED,
        callback: () => {
          onEnded();
          setupAlert({
            key: LiveSessionVideoAlertTypes.TEACHER_STREAMLABS_ERROR,
            show: true,
          });
        },
      },
      {
        userRoles: [UserRoles.TEACHER],
        eventName: player?.getIVSEvents()?.PlayerEventType?.ERROR,
        callback: (error) => {
          const showErrorAlert = isStreamlabsError(error);
          if (showErrorAlert) {
            onStreamError();
            setupAlert({
              key: LiveSessionVideoAlertTypes.TEACHER_STREAMLABS_ERROR,
              show: true,
            });
          }
        },
      },
    ].filter(
      (ivsEvent) =>
        ivsEvent.userRoles.indexOf(isTeacher ? UserRoles.TEACHER : UserRoles.STUDENT) >= 0
    )
  );

  useEffect(() => {
    if (!liveSession?.id) {
      return null;
    }

    let bodyNodes = [];
    const headNodes = [];
    setAlert(alertInitialState);

    const scriptPromises = [
      insertScriptAsync({ url: config.VIDEOJS_SCRIPT_URL }),
      insertScriptAsync({ url: config.AWS_IVS_TECH_SCRIPT_URL }),
    ];

    if (isRecordedLive()) {
      scriptPromises.push(insertScriptAsync({ url: config.AWS_QUALITY_PLUGIN_SCRIPT_URL }));
    }

    Promise.all(scriptPromises)
      .then((scriptNodes) => {
        bodyNodes = scriptNodes;
        setupPlayer();
      })
      .catch((error) => {
        throw new Error(error);
      });

    return () => {
      bodyNodes.forEach((scriptNode) => removeNode(scriptNode));
      headNodes.forEach((styleNode) => removeHeadNode(styleNode));

      if (player) {
        player.dispose();
      }
    };
  }, []);

  useEffect(() => {
    if (player) {
      const ivsEvents = getIVSPlayerEvents();
      ivsEvents.forEach((ivsEvent) => {
        player.getIVSPlayer()?.addEventListener(ivsEvent.eventName, ivsEvent.callback);
      });
    }
  }, [player]);

  return (
    <Flex
      justifyContent="center"
      backgroundColor="black"
      height={height}
      width={width}
      position="relative"
    >
      {alert?.show && <LiveSessionAlert type={alert?.type} message={alert?.message} />}
      <div data-vjs-player>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          playsInline
          ref={videoPlayerRef}
          className="video-js vjs-big-play-centered"
          style={{ width: "100%" }}
        />
      </div>
    </Flex>
  );
};

VideoJSVideoPlayer.propTypes = {
  width: PropTypes.string.isRequired,
  height: PropTypes.string.isRequired,
  onReady: PropTypes.func.isRequired,
  onEnded: PropTypes.func.isRequired,
  onStreamError: PropTypes.func.isRequired,
};

export default VideoJSVideoPlayer;
